import styled from '@emotion/styled'
import { RoomType } from 'common/types'
import { FC, ReactNode } from 'react'
import { LobbyInPerson } from 'room/lobby/lobby-in-person'
import { assertUnreachable } from 'utils/unreachable'
import { InPersonStage } from './in-person-stage'

const Screen = styled.div`
	display: flex;
	flex-direction: column;
	width: 100vw;
	height: 100vh;
	overflow: hidden;
`

/** No header above the stage on the projector: a gap instead. */
const StageArea = styled.div`
	display: flex;
	flex: 1;
	min-height: 0;
	padding-top: 24px;
`

type Props = {
	roomType: RoomType
	/** For rooms an in-person session never puts the trainer in. */
	unsupported: ReactNode
}

/**
 * The trainer's room as the projector shows it in presenter mode: the stage
 * and nothing else - no header, no menu, no misc panel. The trainer drives
 * everything from the console; this window is fed the console's state
 * (`presenter-mode/`) and draws the same stage, whose tools leave out their
 * controls on this surface (`StageControls`).
 */
export const RoomProjectorInPerson: FC<Props> = ({ roomType, unsupported }) => {
	const room = ((): ReactNode => {
		switch (roomType) {
			case RoomType.Lobby:
				return <LobbyInPerson />
			case RoomType.TrainingRoom:
				return (
					<StageArea>
						<InPersonStage data-testid="presenter.projector.stage" />
					</StageArea>
				)
			case RoomType.WebinarRoom:
			case RoomType.GroupRoom:
			case RoomType.PublicChat:
				return unsupported
			default:
				return assertUnreachable('RoomType', roomType)
		}
	})()
	return <Screen>{room}</Screen>
}

export default RoomProjectorInPerson
